import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { Icon } from "semantic-ui-react";
import moment from "moment";
import api from "../../api";

const SECOES = "unidades|responsaveis|votacoes|notificacoes|importacao|historico|comprar";

const iniciais = nome =>
  (nome || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join("");

const status = c => {
  if (!c.dataVencimento) return { texto: "Não ativado", cls: "pendente" };
  if (moment(c.dataVencimento).isBefore(moment())) return { texto: "Vencido", cls: "pendente" };
  return { texto: `Ativo até ${moment(c.dataVencimento).format("MM/YYYY")}`, cls: "ativo" };
};

// Seletor de condomínio: mostra o condomínio atual e permite alternar sem voltar à lista.
const CondominioSwitcher = ({ condominio }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [aberto, setAberto] = useState(false);
  const [lista, setLista] = useState(null);
  const [busca, setBusca] = useState("");
  const raiz = useRef(null);

  const atualId = condominio && condominio._id;

  useEffect(() => {
    if (!aberto || lista) return;
    api.condominio.findAll().then(l => setLista(l.filter(c => c.ativo !== false))).catch(() => setLista([]));
  }, [aberto, lista]);

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = e => raiz.current && !raiz.current.contains(e.target) && setAberto(false);
    const esc = e => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);

  const filtrada = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (lista || []).filter(c => !termo || (c.nome || "").toLowerCase().includes(termo));
  }, [lista, busca]);

  const selecionar = c => {
    setAberto(false);
    setBusca("");
    if (c._id === atualId) return;
    // mantém a seção atual (ex.: Votações) ao trocar de condomínio
    const secao = pathname.match(new RegExp(`^/condominios/[^/]+/(${SECOES})/?$`));
    history.push(`/condominios/${c._id}${secao ? `/${secao[1]}` : ""}`);
  };

  const temNome = !!(condominio && condominio.nome);

  return (
    <div className="va-switcher" ref={raiz}>
      <button
        type="button"
        className={`va-switcher-botao ${aberto ? "aberto" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        onClick={() => setAberto(v => !v)}
      >
        <span className="va-switcher-avatar">{temNome ? iniciais(condominio.nome) : <Icon name="building" />}</span>
        <span className="va-switcher-texto">
          <small>Condomínio</small>
          <b>{temNome ? condominio.nome : "Selecionar condomínio"}</b>
        </span>
        <Icon name="chevron down" className="va-switcher-seta" />
      </button>

      {aberto && (
        <div className="va-switcher-painel" role="listbox">
          {(lista || []).length > 5 && (
            <div className="va-switcher-busca">
              <Icon name="search" />
              <input autoFocus placeholder="Buscar condomínio..." value={busca} onChange={e => setBusca(e.target.value)} />
            </div>
          )}
          <div className="va-switcher-lista">
            {lista === null && <div className="va-switcher-vazio">Carregando...</div>}
            {lista !== null && filtrada.length === 0 && <div className="va-switcher-vazio">Nenhum condomínio encontrado</div>}
            {filtrada.map(c => {
              const st = status(c);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={c._id === atualId}
                  key={c._id}
                  className={`va-switcher-item ${c._id === atualId ? "atual" : ""}`}
                  onClick={() => selecionar(c)}
                >
                  <span className="va-switcher-avatar pequeno">{iniciais(c.nome)}</span>
                  <span className="va-switcher-texto">
                    <b>{c.nome}</b>
                    <small className={st.cls}>{st.texto}</small>
                  </span>
                  {c._id === atualId && <Icon name="check" className="va-switcher-check" />}
                </button>
              );
            })}
          </div>
          <div className="va-switcher-rodape">
            <button type="button" onClick={() => { setAberto(false); history.push("/condominios"); }}>
              <Icon name="th large" /> Ver todos
            </button>
            <button type="button" onClick={() => { setAberto(false); history.push("/condominios/cadastrar"); }}>
              <Icon name="plus" /> Novo condomínio
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CondominioSwitcher.propTypes = {
  condominio: PropTypes.shape({ _id: PropTypes.string, nome: PropTypes.string })
};

export default CondominioSwitcher;
